import React, { useEffect, useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { transportApi, downloadCsv } from '@/services/transportApi';

const DAY_STYLE = {
  present: 'bg-emerald-500', open: 'bg-amber-400', missed: 'bg-orange-500', auto: 'bg-indigo-500', hr: 'bg-red-500',
  absent: 'bg-gray-200', future: 'bg-white border border-gray-200',
};
const DAY_LABEL = { present: 'Present', open: 'On duty', missed: 'Missed check-out (HR)', auto: 'Auto-closed 12 AM (older days)', hr: 'Contact HR (older days)', absent: 'Absent', future: '' };

const Legend = () => (
  <div className="flex flex-wrap gap-3 text-[11px] text-gray-500" data-testid="monthly-legend">
    {['present', 'open', 'missed', 'auto', 'hr', 'absent'].map((k) => (
      <span key={k} className="inline-flex items-center gap-1"><span className={`w-2.5 h-2.5 rounded-sm ${DAY_STYLE[k]}`} />{DAY_LABEL[k]}</span>
    ))}
  </div>
);

const DayStrip = ({ days, did }) => (
  <div className="flex gap-[3px]" data-testid={`monthly-strip-${did}`}>
    {days.map((d) => (
      <span key={d.date} title={`${d.date.slice(8)} · ${DAY_LABEL[d.status] || '—'}${d.hours ? ` · ${d.hours} h` : ''}`}
        className={`w-2.5 h-5 rounded-sm ${DAY_STYLE[d.status]}`} data-testid={`monthly-day-${did}-${d.date.slice(8)}`} data-status={d.status} />
    ))}
  </div>
);

export const MonthlyAttendance = () => {
  const [month, setMonth] = useState(new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }).slice(0, 7));
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setData(null); transportApi.attendanceMonthly(month).then(setData).catch(() => toast.error('Failed to load monthly attendance')); }, [month]);

  const exportCsv = async () => {
    setBusy(true);
    try { await downloadCsv(`/transport/attendance/monthly?month=${month}&format=csv`, `driver_attendance_monthly_${month}.csv`); }
    catch { toast.error('Export failed'); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm text-gray-600">Month</label>
        <Input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} className="max-w-[180px]" data-testid="monthly-month-input" />
        <Legend />
        <Button onClick={exportCsv} disabled={busy} variant="outline" className="ml-auto border-amber-400 text-amber-700" data-testid="monthly-export-btn">
          {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}Download CSV
        </Button>
      </div>
      <Card className="border-0 shadow-md"><CardContent className="p-0 overflow-x-auto">
        <Table data-testid="monthly-table">
          <TableHeader><TableRow>
            <TableHead>Driver</TableHead><TableHead className="text-right">Present</TableHead><TableHead className="text-right">Absent</TableHead>
            <TableHead className="text-right">Hours</TableHead><TableHead className="text-right">Auto-closed</TableHead><TableHead className="text-right">Missed / HR</TableHead>
            <TableHead>Days</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {(data?.drivers || []).map((r) => (
              <TableRow key={r.driver_id} data-testid={`monthly-row-${r.driver_id}`}>
                <TableCell className="font-semibold whitespace-nowrap">{r.driver_name}</TableCell>
                <TableCell className="text-right font-semibold text-emerald-700" data-testid={`monthly-present-${r.driver_id}`}>{r.days_present}</TableCell>
                <TableCell className="text-right text-gray-500" data-testid={`monthly-absent-${r.driver_id}`}>{r.absent_days}</TableCell>
                <TableCell className="text-right" data-testid={`monthly-hours-${r.driver_id}`}>{r.total_hours}</TableCell>
                <TableCell className="text-right text-indigo-700" data-testid={`monthly-auto-${r.driver_id}`}>{r.auto_closed_days}</TableCell>
                <TableCell className="text-right text-red-600" data-testid={`monthly-hr-${r.driver_id}`}>{r.needs_hr_days}</TableCell>
                <TableCell><DayStrip days={r.days} did={r.driver_id} /></TableCell>
              </TableRow>
            ))}
            {!data && <TableRow><TableCell colSpan={7} className="text-center text-gray-400 py-8"><Loader2 className="w-4 h-4 inline mr-1 animate-spin" />Loading…</TableCell></TableRow>}
            {data && data.drivers.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-gray-400 py-8" data-testid="monthly-empty">No drivers for this month.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
};

export default MonthlyAttendance;
